/**
 * TradingPanel Component
 * 
 * Displays Recent Trades and Order Book side by side
 * Replaces the chart for a more trading-terminal style view
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { ExternalLink, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { getPoolTrades, type OrderbookTrade } from '../../utils/blockscoutApi';

// ==================== TYPES ====================

interface TradingPanelProps {
  poolAddress: string | null;
  tokenAddress: string | null;
  tokenSymbol: string;
  ethPriceUSD?: number;
  refreshInterval?: number;
}

interface PriceLevel {
  price: number;
  volume: number;
  cumulativeVolume: number;
  count: number;
}

// ==================== ANIMATIONS ====================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ==================== STYLED COMPONENTS ====================

const Container = styled.div`
  display: flex;
  gap: 12px;
  height: 500px;
  max-height: 500px;
  
  @media (max-width: 900px) {
    flex-direction: column;
    height: auto;
    max-height: none;
  }
`;

const Panel = styled.div`
  flex: 1;
  background: #09090b;
  border: 1px solid #27272a;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 100%;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #27272a;
  background: #18181b;
`;

const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #fafafa;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LiveIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #22c55e;
  font-weight: 500;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const RefreshButton = styled.button<{ $spinning?: boolean }>`
  background: transparent;
  border: none;
  color: #71717a;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    color: #a1a1aa;
    background: #27272a;
  }
  
  svg {
    animation: ${props => props.$spinning ? spin : 'none'} 1s linear infinite;
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0; /* Important for flex scroll */
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #09090b;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #27272a;
    border-radius: 2px;
  }
`;

// Trades List Styles
const TradesTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 55px 1fr 90px 50px 30px;
  gap: 4px;
  padding: 8px 12px;
  background: #0a0a0a;
  border-bottom: 1px solid #1f1f23;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const HeaderCell = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #52525b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TradeRow = styled.div<{ $isNew?: boolean }>`
  display: grid;
  grid-template-columns: 55px 1fr 90px 50px 30px;
  gap: 4px;
  padding: 8px 12px;
  border-bottom: 1px solid #1a1a1e;
  transition: background 0.2s;
  animation: ${props => props.$isNew ? fadeIn : 'none'} 0.3s ease-out;
  
  &:hover {
    background: #18181b;
  }
`;

const TypeBadge = styled.span<{ $isBuy: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.$isBuy ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)'};
  color: ${props => props.$isBuy ? '#22c55e' : '#ef4444'};
`;

const Cell = styled.span`
  font-size: 11px;
  color: #d4d4d8;
  display: flex;
  align-items: center;
`;

const AmountCell = styled(Cell)`
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
`;

const AmountPrimary = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #fafafa;
`;

const AmountSecondary = styled.span`
  font-size: 9px;
  color: #71717a;
`;

const PriceCell = styled(Cell)`
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: #a1a1aa;
`;

const TimeCell = styled(Cell)`
  color: #52525b;
  font-size: 10px;
`;

const ExplorerLink = styled.a`
  color: #52525b;
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    color: #a78bfa;
    background: rgba(167, 139, 250, 0.1);
  }
`;

// Orderbook Styles
const OrderbookContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const OrderbookSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const OrderbookHeader = styled.div<{ $isBuy: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.$isBuy ? '#22c55e' : '#ef4444'};
  background: #0a0a0a;
  border-bottom: 1px solid #1f1f23;
`;

const OrderbookLevels = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #27272a;
    border-radius: 2px;
  }
`;

const LevelRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
  padding: 0 12px;
  font-size: 11px;
  border-bottom: 1px solid #1a1a1e;
`;

const LevelBar = styled.div<{ $width: number; $isBuy: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: ${props => Math.min(props.$width, 100)}%;
  background: ${props => props.$isBuy 
    ? 'rgba(34, 197, 94, 0.12)'
    : 'rgba(239, 68, 68, 0.12)'};
  transition: width 0.3s ease;
`;

const LevelPrice = styled.span`
  position: relative;
  z-index: 1;
  color: #a1a1aa;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
`;

const LevelVolume = styled.span<{ $isBuy: boolean }>`
  position: relative;
  z-index: 1;
  color: ${props => props.$isBuy ? '#22c55e' : '#ef4444'};
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
`;

const SpreadRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 12px;
  background: #18181b;
  font-size: 11px;
  color: #71717a;
  gap: 12px;
  border-top: 1px solid #27272a;
  border-bottom: 1px solid #27272a;
`;

const SpreadValue = styled.span`
  color: #fafafa;
  font-weight: 600;
`;

const TotalVolumes = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 10px 12px;
  background: #0a0a0a;
  border-top: 1px solid #1f1f23;
`;

const VolumeBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const VolumeLabel = styled.span`
  font-size: 9px;
  color: #52525b;
  text-transform: uppercase;
`;

const VolumeValue = styled.span<{ $isBuy: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$isBuy ? '#22c55e' : '#ef4444'};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #52525b;
  font-size: 13px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #52525b;
  font-size: 13px;
  gap: 8px;
  
  svg {
    animation: ${spin} 1s linear infinite;
  }
`;

// ==================== HELPERS ====================

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function formatAmount(amount: number): string {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  if (amount >= 1) return amount.toFixed(1);
  return amount.toFixed(2);
}

function formatPrice(price: number): string {
  if (price < 0.000001) return price.toExponential(2);
  if (price < 0.0001) return `$${price.toFixed(8)}`;
  if (price < 0.01) return `$${price.toFixed(6)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(2)}`;
}

function formatUSD(amount: number): string {
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount.toFixed(2)}`;
}

function formatVolume(volume: number): string {
  if (volume >= 1000000) return `${(volume / 1000000).toFixed(1)}M`;
  if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
  return volume.toFixed(0);
}

function aggregateByPrice(
  trades: OrderbookTrade[],
  type: 'buy' | 'sell',
  levels: number
): PriceLevel[] {
  const filtered = trades.filter(t => t.type === type);
  if (filtered.length === 0) return [];

  const prices = filtered.map(t => t.priceUSD);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  
  if (minPrice === maxPrice) {
    const totalVolume = filtered.reduce((sum, t) => sum + t.tokenAmount, 0);
    return [{
      price: minPrice,
      volume: totalVolume,
      cumulativeVolume: totalVolume,
      count: filtered.length,
    }];
  }

  const bucketSize = (maxPrice - minPrice) / levels;
  const buckets: Map<number, { volume: number; count: number }> = new Map();

  for (const trade of filtered) {
    const bucketIndex = Math.floor((trade.priceUSD - minPrice) / bucketSize);
    const bucketPrice = minPrice + (bucketIndex + 0.5) * bucketSize;
    const roundedPrice = parseFloat(bucketPrice.toPrecision(4));
    
    const existing = buckets.get(roundedPrice) || { volume: 0, count: 0 };
    existing.volume += trade.tokenAmount;
    existing.count += 1;
    buckets.set(roundedPrice, existing);
  }

  const result: PriceLevel[] = [];
  let cumulative = 0;

  const sortedPrices = [...buckets.keys()].sort((a, b) => 
    type === 'buy' ? b - a : a - b
  );

  for (const price of sortedPrices) {
    const bucket = buckets.get(price)!;
    cumulative += bucket.volume;
    result.push({
      price,
      volume: bucket.volume,
      cumulativeVolume: cumulative,
      count: bucket.count,
    });
  }

  return result.slice(0, levels);
}

// ==================== COMPONENT ====================

const TradingPanel: React.FC<TradingPanelProps> = ({
  poolAddress,
  tokenAddress,
  tokenSymbol,
  ethPriceUSD = 3500,
  refreshInterval = 30000,
}) => {
  const [trades, setTrades] = useState<OrderbookTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute orderbook data
  const orderbookData = useMemo(() => {
    const buyLevels = aggregateByPrice(trades, 'buy', 8);
    const sellLevels = aggregateByPrice(trades, 'sell', 8);
    
    const totalBuyVolume = trades.filter(t => t.type === 'buy').reduce((sum, t) => sum + t.tokenAmount, 0);
    const totalSellVolume = trades.filter(t => t.type === 'sell').reduce((sum, t) => sum + t.tokenAmount, 0);
    
    const buyPrices = trades.filter(t => t.type === 'buy').map(t => t.priceUSD);
    const sellPrices = trades.filter(t => t.type === 'sell').map(t => t.priceUSD);
    
    const bestBid = buyPrices.length > 0 ? Math.max(...buyPrices) : 0;
    const bestAsk = sellPrices.length > 0 ? Math.min(...sellPrices) : 0;
    const spread = bestAsk > 0 && bestBid > 0 ? ((bestAsk - bestBid) / bestBid) * 100 : 0;
    
    const maxVolume = Math.max(
      ...buyLevels.map(l => l.cumulativeVolume),
      ...sellLevels.map(l => l.cumulativeVolume),
      1
    );
    
    return { buyLevels, sellLevels, totalBuyVolume, totalSellVolume, bestBid, bestAsk, spread, maxVolume };
  }, [trades]);

  const fetchTrades = useCallback(async (isRefresh = false) => {
    if (!poolAddress || !tokenAddress) {
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const fetchedTrades = await getPoolTrades(poolAddress, tokenAddress, ethPriceUSD);
      setTrades(fetchedTrades);
      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      console.error('[TradingPanel] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch trades');
      setLoading(false);
      setRefreshing(false);
    }
  }, [poolAddress, tokenAddress, ethPriceUSD]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades]);

  useEffect(() => {
    if (refreshInterval <= 0 || !poolAddress || !tokenAddress) return;
    const interval = setInterval(() => fetchTrades(true), refreshInterval);
    return () => clearInterval(interval);
  }, [fetchTrades, refreshInterval, poolAddress, tokenAddress]);

  const handleRefresh = () => {
    fetchTrades(true);
  };

  if (!poolAddress || !tokenAddress) {
    return (
      <Container>
        <Panel>
          <PanelHeader>
            <PanelTitle>Trading Activity</PanelTitle>
          </PanelHeader>
          <EmptyState>No pool data available</EmptyState>
        </Panel>
      </Container>
    );
  }

  return (
    <Container>
      {/* Recent Trades Panel */}
      <Panel>
        <PanelHeader>
          <PanelTitle>
            Recent Trades
            {trades.length > 0 && <LiveIndicator>LIVE</LiveIndicator>}
          </PanelTitle>
          <RefreshButton onClick={handleRefresh} $spinning={refreshing} title="Refresh">
            <RefreshCw size={14} />
          </RefreshButton>
        </PanelHeader>
        <PanelContent>
          {loading ? (
            <LoadingState>
              <RefreshCw size={20} />
              Loading trades...
            </LoadingState>
          ) : error ? (
            <EmptyState>{error}</EmptyState>
          ) : trades.length === 0 ? (
            <EmptyState>No trades found</EmptyState>
          ) : (
            <TradesTable>
              <TableHeader>
                <HeaderCell>Type</HeaderCell>
                <HeaderCell>Amount</HeaderCell>
                <HeaderCell>Price</HeaderCell>
                <HeaderCell>Time</HeaderCell>
                <HeaderCell></HeaderCell>
              </TableHeader>
              {trades.slice(0, 30).map((trade, index) => (
                <TradeRow key={`${trade.txHash}-${index}`} $isNew={index < 3}>
                  <Cell>
                    <TypeBadge $isBuy={trade.type === 'buy'}>
                      {trade.type === 'buy' ? (
                        <><TrendingUp size={9} /> BUY</>
                      ) : (
                        <><TrendingDown size={9} /> SELL</>
                      )}
                    </TypeBadge>
                  </Cell>
                  <AmountCell>
                    <AmountPrimary>{formatAmount(trade.tokenAmount)} {tokenSymbol}</AmountPrimary>
                    <AmountSecondary>{formatUSD(trade.amountUSD)}</AmountSecondary>
                  </AmountCell>
                  <PriceCell>{formatPrice(trade.priceUSD)}</PriceCell>
                  <TimeCell>{formatTimeAgo(trade.timestamp)}</TimeCell>
                  <Cell>
                    <ExplorerLink 
                      href={`https://explorer.inkonchain.com/tx/${trade.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink size={10} />
                    </ExplorerLink>
                  </Cell>
                </TradeRow>
              ))}
            </TradesTable>
          )}
        </PanelContent>
      </Panel>

      {/* Order Book Panel */}
      <Panel>
        <PanelHeader>
          <PanelTitle>
            Order Book
            {trades.length > 0 && <LiveIndicator>LIVE</LiveIndicator>}
          </PanelTitle>
          <span style={{ fontSize: '10px', color: '#52525b' }}>
            {trades.length} trades
          </span>
        </PanelHeader>
        <PanelContent>
          {loading ? (
            <LoadingState>
              <RefreshCw size={20} />
              Loading...
            </LoadingState>
          ) : error ? (
            <EmptyState>{error}</EmptyState>
          ) : trades.length === 0 ? (
            <EmptyState>No data</EmptyState>
          ) : (
            <OrderbookContainer>
              {/* Asks (Sells) */}
              <OrderbookSide>
                <OrderbookHeader $isBuy={false}>
                  <span>Price (USD)</span>
                  <span>Size ({tokenSymbol})</span>
                </OrderbookHeader>
                <OrderbookLevels>
                  {orderbookData.sellLevels.slice().reverse().map((level, i) => (
                    <LevelRow key={`sell-${i}`}>
                      <LevelBar 
                        $width={(level.cumulativeVolume / orderbookData.maxVolume) * 100}
                        $isBuy={false}
                      />
                      <LevelPrice>{formatPrice(level.price)}</LevelPrice>
                      <LevelVolume $isBuy={false}>{formatVolume(level.volume)}</LevelVolume>
                    </LevelRow>
                  ))}
                  {orderbookData.sellLevels.length === 0 && (
                    <LevelRow>
                      <span style={{ color: '#52525b', fontSize: '10px' }}>No asks</span>
                    </LevelRow>
                  )}
                </OrderbookLevels>
              </OrderbookSide>

              {/* Spread */}
              <SpreadRow>
                <span>Spread: <SpreadValue>{orderbookData.spread.toFixed(2)}%</SpreadValue></span>
              </SpreadRow>

              {/* Bids (Buys) */}
              <OrderbookSide>
                <OrderbookLevels>
                  {orderbookData.buyLevels.map((level, i) => (
                    <LevelRow key={`buy-${i}`}>
                      <LevelBar 
                        $width={(level.cumulativeVolume / orderbookData.maxVolume) * 100}
                        $isBuy={true}
                      />
                      <LevelPrice>{formatPrice(level.price)}</LevelPrice>
                      <LevelVolume $isBuy={true}>{formatVolume(level.volume)}</LevelVolume>
                    </LevelRow>
                  ))}
                  {orderbookData.buyLevels.length === 0 && (
                    <LevelRow>
                      <span style={{ color: '#52525b', fontSize: '10px' }}>No bids</span>
                    </LevelRow>
                  )}
                </OrderbookLevels>
              </OrderbookSide>

              {/* Total Volumes */}
              <TotalVolumes>
                <VolumeBox>
                  <VolumeLabel>Bid Volume</VolumeLabel>
                  <VolumeValue $isBuy={true}>{formatVolume(orderbookData.totalBuyVolume)}</VolumeValue>
                </VolumeBox>
                <VolumeBox>
                  <VolumeLabel>Ask Volume</VolumeLabel>
                  <VolumeValue $isBuy={false}>{formatVolume(orderbookData.totalSellVolume)}</VolumeValue>
                </VolumeBox>
              </TotalVolumes>
            </OrderbookContainer>
          )}
        </PanelContent>
      </Panel>
    </Container>
  );
};

export default TradingPanel;

